import type { OrderDetail } from '~/types/order/OrderDetail'
import type { OrderDetailNormalized } from '~/interfaces/order/OrderDetailNormalized'
import type { OrderItem } from '~/types/order/OrderItem'
import type { OrderModificationRequest } from '~/interfaces/order/OrderModificationRequest'
import type { OrderModificationRequestOrderSnapshot } from '~/types/order/OrderModificationRequestOrderSnapshot'
import type { OrderLog } from '~/types/order/OrderLog'
import { OrderStatusSchema, OrderTypeSchema } from '~/services/api/generated'
import type {
  CustomerOrderData,
  OrderItemData,
  OrderLedgerEntryDto,
  OrderModificationOrderSnapshot,
  OrderModificationRequestData,
  PartnerOrderData,
  StoreSummary
} from '~/services/api/generated'
import {
  type DeliveryContactType,
  DeliveryContactTypeValues,
  OrderLogActionType,
  OrderLogActionTypeValues
} from '~/enums'
import { Normalization } from '~/utils/normalization'

const normalizeDeliveryContactType = (value: string | null): DeliveryContactType | null => {
  if (value === null) return null
  const contactType = Normalization.enumValue(value, DeliveryContactTypeValues)
  if (contactType === null) {
    throw new TypeError('Invalid order response: delivery_contact_type is not a known type.')
  }
  return contactType
}

// The API sends null for an unloaded requester and '' for a nameless one; both mean no booker.
const normalizeBookedBy = (value: string | null): string | undefined =>
  value !== null && value !== '' ? value : undefined

const normalizeSnapshotInt = (value: string | number | null, field: string): number | null => {
  if (value === null) return null
  const parsed = Normalization.floatValue(value, null)
  if (parsed === null || !Number.isInteger(parsed)) {
    throw new TypeError(`Invalid order response: modification snapshot ${field} is not an integer.`)
  }
  return parsed
}

const normalizeRequestTotal = (value: string, field: string): number => {
  const parsed = Normalization.floatValue(value, null)
  if (parsed === null || !Number.isFinite(parsed)) {
    throw new TypeError(
      `Invalid order response: modification request ${field} is not a finite number.`
    )
  }
  return parsed
}

const requireStore = (store: StoreSummary | null): StoreSummary => {
  if (store === null) throw new TypeError('Invalid order response: store must be present.')
  return store
}

const normalizeOrderItem = (item: OrderItemData): OrderItem => ({
  id: item.id,
  item_name: item.item_name,
  quantity: item.quantity,
  unit_price: item.unit_price,
  total_price: item.total_price,
  customizations: item.customizations,
  special_instructions: item.special_instructions,
  tax_category: item.tax_category,
  menu_item: { id: item.menu_item.id, name: item.menu_item.name }
})

const normalizeOrderLog = (entry: OrderLedgerEntryDto): OrderLog => {
  const actionType = Normalization.enumValue(entry.action_type, OrderLogActionTypeValues)
  if (actionType === null) {
    throw new TypeError('Invalid order response: timeline action_type is not a known action.')
  }
  const common = {
    id: entry.id,
    action: entry.action,
    field_name: entry.field_name,
    old_value: entry.old_value,
    description: entry.description,
    metadata: entry.metadata,
    user: entry.user,
    created_at: entry.created_at
  }

  if (
    actionType === OrderLogActionType.STATUS_CHANGE ||
    actionType === OrderLogActionType.CANCELLATION
  ) {
    return {
      ...common,
      action_type: actionType,
      new_value: OrderStatusSchema.parse(entry.new_value)
    }
  }

  return { ...common, action_type: actionType, new_value: entry.new_value }
}

const normalizeOrderModificationRequestOrderSnapshot = (
  snapshot: OrderModificationOrderSnapshot
): OrderModificationRequestOrderSnapshot => ({
  tip: snapshot.tip,
  requested_date: snapshot.requested_date,
  requested_time: snapshot.requested_time,
  order_type: OrderTypeSchema.nullable().parse(snapshot.order_type),
  guest_count: normalizeSnapshotInt(snapshot.guest_count, 'guest_count'),
  setup_for_me: snapshot.setup_for_me,
  delivery_phone: snapshot.delivery_phone,
  delivery_suite: snapshot.delivery_suite,
  delivery_address: snapshot.delivery_address,
  payment_method_id: normalizeSnapshotInt(snapshot.payment_method_id, 'payment_method_id'),
  special_instructions: snapshot.special_instructions,
  delivery_contact_type: normalizeDeliveryContactType(snapshot.delivery_contact_type),
  delivery_business_name: snapshot.delivery_business_name,
  delivery_contact_phone: snapshot.delivery_contact_phone,
  delivery_contact_person: snapshot.delivery_contact_person
})

const normalizeOrderModificationRequest = (
  request: OrderModificationRequestData
): OrderModificationRequest => ({
  id: request.id,
  order_id: request.order_id,
  order_number: request.order_number,
  user_id: request.user_id,
  store_id: request.store_id,
  status: request.status,
  details_changed: request.details_changed,
  items_changed: request.items_changed,
  previous_total: normalizeRequestTotal(request.previous_total, 'previous_total'),
  proposed_total: normalizeRequestTotal(request.proposed_total, 'proposed_total'),
  proposed_pricing: request.proposed_pricing,
  previous_order: normalizeOrderModificationRequestOrderSnapshot(request.previous_order),
  proposed_order: normalizeOrderModificationRequestOrderSnapshot(request.proposed_order),
  previous_items: request.previous_items,
  proposed_items: request.proposed_items,
  rejection_reason: request.rejection_reason,
  reviewed_by: request.reviewed_by,
  reviewer: request.reviewer,
  created_at: request.created_at,
  applied_at: request.applied_at,
  reviewed_at: request.reviewed_at
})

export const normalizeOrderDetail = (source: CustomerOrderData | PartnerOrderData): OrderDetail => {
  const { store, delivery_contact_type, timeline, order_items, modification_requests, ...rest } =
    source
  const parts: OrderDetailNormalized = Normalization.omitUndefined({
    store: requireStore(store),
    delivery_contact_type: normalizeDeliveryContactType(delivery_contact_type),
    timeline: timeline?.map(normalizeOrderLog),
    order_items: order_items?.map(normalizeOrderItem),
    modification_requests: modification_requests?.map(normalizeOrderModificationRequest)
  })
  if ('booked_by' in rest) {
    const { booked_by, ...customerRest } = rest
    return {
      ...customerRest,
      ...parts,
      ...Normalization.omitUndefined({ booked_by: normalizeBookedBy(booked_by) })
    }
  }
  return { ...rest, ...parts }
}
